import React from "react";
import PropTypes from "prop-types";
import { Descriptions, Tag } from "antd";

import DashedTooltip from "components/UI/DashedTooltip";
import {
  formatDay,
  formatIv,
  formatMarketDateTimeText,
  formatPercent,
  formatPoints,
  formatPrice,
} from "./optionStrategyUtils";
import { returnUpDownClassname } from "utils/helpers";

const colored = (format) => (value) => (
  <span className={returnUpDownClassname(value)}>{format(value)}</span>
);
const percent = colored(formatPercent);
const points = colored(formatPoints);
// a cost: positive (paid above mid / sold below it) is bad
const costPercent = (value) => (
  <span className={returnUpDownClassname(value == null ? value : -value)}>
    {formatPercent(value)}
  </span>
);

const bidAsk = (bid, ask) =>
  bid == null && ask == null
    ? "-"
    : `${formatPrice(bid)} / ${formatPrice(ask)}`;

const Section = ({ title, items }) => (
  <Descriptions
    size="small"
    bordered
    column={1}
    title={title}
    items={items.map(([label, children], index) => ({
      key: index,
      label,
      children,
    }))}
  />
);

Section.propTypes = {
  title: PropTypes.node.isRequired,
  items: PropTypes.array.isRequired,
};

// Expanded row of the option trades history: what the underlying did, how the
// model priced that move, where the gap to the real result came from, the
// fills against the 15:50 quotes and the timing.
const OptionTradeBreakdown = ({ trade }) => {
  if (!trade.breakdown) return null;
  const { underlying, iv, model, fills, timing } = trade.breakdown;

  return (
    <div className="grid gap-4 p-2 md:grid-cols-2 xl:grid-cols-4">
      <Section
        title="Underlying"
        items={[
          ["At signal", formatPrice(underlying.signal)],
          ["At open", formatPrice(underlying.open)],
          ["At exit", formatPrice(underlying.close)],
          ["Move", percent(underlying.movePercent)],
          [
            "Target",
            <>
              {formatPrice(underlying.target)} (
              {formatPercent(underlying.targetPercent)}){" "}
              {underlying.targetHit != null && (
                <Tag color={underlying.targetHit ? "green" : "default"}>
                  {underlying.targetHit ? "hit" : "not hit"}
                </Tag>
              )}
            </>,
          ],
        ]}
      />
      <Section
        title={
          <DashedTooltip text="Black-Scholes at the actual underlying price at exit. In points of the entry premium: actual - model at open IV = IV effect + execution">
            Model check
          </DashedTooltip>
        }
        items={[
          [
            "Model, open IV",
            <>
              {formatPrice(model.openIvPrice)} ({percent(model.openIvPercent)})
            </>,
          ],
          [
            "Model, exit IV",
            <>
              {formatPrice(model.exitIvPrice)} ({percent(model.exitIvPercent)})
            </>,
          ],
          ["Actual (gross)", percent(model.grossPercent)],
          ["Gap to model", points(model.gapPoints)],
          [
            <DashedTooltip key="iv" text="Model at exit IV - model at open IV">
              IV effect
            </DashedTooltip>,
            points(model.ivEffectPoints),
          ],
          [
            <DashedTooltip
              key="execution"
              text="Actual - model at exit IV: the spread, the fill vs the 15:50 quote, and what the model misses"
            >
              Execution
            </DashedTooltip>,
            points(model.executionPoints),
          ],
          ["Fees", points(model.feesPoints)],
          ["Actual (net)", percent(model.netPercent)],
        ]}
      />
      <Section
        title={
          <DashedTooltip text="Fills against the 15:50 quotes the buy / exit signal was based on">
            Fills
          </DashedTooltip>
        }
        items={[
          ["Signal bid / ask", bidAsk(fills.signalBid, fills.signalAsk)],
          ["Signal spread", formatPercent(fills.signalSpreadPercent)],
          [
            <DashedTooltip
              key="entry"
              text="Entry fill vs the signal quote's mid: above 0 = paid more than mid"
            >
              Entry vs mid
            </DashedTooltip>,
            costPercent(fills.entryVsMidPercent),
          ],
          ["Exit bid / ask", bidAsk(fills.exitBid, fills.exitAsk)],
          ["Exit spread", formatPercent(fills.exitSpreadPercent)],
          [
            <DashedTooltip
              key="exit"
              text="Exit fill vs the exit quote's mid: above 0 = sold above mid"
            >
              Exit vs mid
            </DashedTooltip>,
            percent(fills.exitVsMidPercent),
          ],
          ["IV open → exit", `${formatIv(iv.open)} → ${formatIv(iv.close)}`],
        ]}
      />
      <Section
        title="Timing"
        items={[
          ["Signal", formatDay(trade.advicedBuyDate)],
          ["Opened", formatMarketDateTimeText(trade.openDate)],
          ["Closed", formatMarketDateTimeText(trade.closeDate)],
          ["Expiration", formatDay(trade.optionTrade?.expirationDate)],
          ["Bars held", timing.bars ?? "-"],
          ["DTE at entry", timing.dteAtEntry ?? "-"],
          ["DTE at exit", timing.dteAtExit ?? "-"],
        ]}
      />
    </div>
  );
};

OptionTradeBreakdown.propTypes = {
  trade: PropTypes.object.isRequired,
};

export default OptionTradeBreakdown;
